import type {
  FloatingContext,
  ReferenceType,
  UseTransitionStylesProps,
} from '@floating-ui/react';
import {useTransitionStyles} from '@floating-ui/react';
import {useReducedMotion} from '@pinecast/styles';
import type * as React from 'react';

type Styles = UseTransitionStylesProps['initial'];

const withoutTransform = (styles: Styles): Styles => {
  if (typeof styles === 'function') {
    return params => withoutTransform(styles(params)) as React.CSSProperties;
  }
  if (!styles) {
    return styles;
  }
  const {transform: _transform, ...rest} = styles;
  return rest;
};

/**
 * Floating UI's `useTransitionStyles`, minus the movement for viewers who asked
 * the OS to minimize motion: under `prefers-reduced-motion: reduce` every
 * `transform` (the scale or slide a popover opens with) is dropped, so the
 * element only fades.
 *
 * The transition styles are inline, which is why this goes through
 * `useReducedMotion` rather than a `REDUCED_MOTION` block: a class can't
 * override an inline style. Floating UI derives `transitionProperty` from the
 * keys of `initial`, so dropping the key stops the transform from animating
 * too, not only from being set.
 */
export const useMotionSafeTransitionStyles = <
  RT extends ReferenceType = ReferenceType,
>(
  context: FloatingContext<RT>,
  props: UseTransitionStylesProps,
) => {
  const reducedMotion = useReducedMotion();
  return useTransitionStyles(
    context,
    reducedMotion
      ? {
          ...props,
          close: withoutTransform(props.close),
          common: withoutTransform(props.common),
          initial: withoutTransform(props.initial),
          open: withoutTransform(props.open),
        }
      : props,
  );
};
